import { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Plus, Trash2 } from "lucide-react";

interface Row {
  key: string;
  value: string;
}

interface NoSqlAttributesEditorProps {
  /** Current attributes as a JSON-encoded object string (or null/empty). */
  value?: string | null;
  /** Called with the re-serialized JSON string (or "" when there are no rows). */
  onChange: (json: string) => void;
  disabled?: boolean;
}

/**
 * Generic key/value editor for the engine-agnostic `attributes` bag on NoSQL
 * nodes (ttl, shardKey, ref, analyzer, resourceType, …). Edits a flat map of
 * string values and serializes it to the JSON string the backend stores.
 * Non-object / unparseable input is surfaced as a single note and left as-is
 * on save until the user edits it.
 */
export default function NoSqlAttributesEditor({
  value,
  onChange,
  disabled,
}: NoSqlAttributesEditorProps) {
  const [rows, setRows] = useState<Row[]>([]);

  // Parse the incoming JSON string into editable rows whenever it changes
  // identity (dialog open / switching the edited node).
  useEffect(() => {
    if (!value) {
      setRows([]);
      return;
    }
    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        setRows(
          Object.entries(parsed).map(([key, v]) => ({
            key,
            value: typeof v === "string" ? v : JSON.stringify(v),
          })),
        );
        return;
      }
    } catch {
      /* fall through to empty */
    }
    setRows([]);
  }, [value]);

  const emit = (next: Row[]) => {
    setRows(next);
    const obj: Record<string, string> = {};
    for (const r of next) {
      const k = r.key.trim();
      if (k) obj[k] = r.value;
    }
    onChange(Object.keys(obj).length ? JSON.stringify(obj) : "");
  };

  const updateRow = (idx: number, patch: Partial<Row>) => {
    emit(rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const addRow = () => emit([...rows, { key: "", value: "" }]);
  const removeRow = (idx: number) => emit(rows.filter((_, i) => i !== idx));

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-white">Attributes</label>
        {!disabled && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addRow}
            className="h-7 gap-1 text-xs"
          >
            <Plus className="h-3.5 w-3.5" /> Add
          </Button>
        )}
      </div>
      <p className="text-xs text-muted">
        Engine-specific detail (e.g. ttl, shardKey, ref, analyzer, resourceType).
        Stored as a JSON object.
      </p>
      {rows.length === 0 ? (
        <div className="text-xs text-muted py-1">No attributes.</div>
      ) : (
        <div className="space-y-2">
          {rows.map((row, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <Input
                value={row.key}
                onChange={(e) => updateRow(idx, { key: e.target.value })}
                placeholder="key"
                disabled={disabled}
                className="bg-surface text-white flex-1"
              />
              <Input
                value={row.value}
                onChange={(e) => updateRow(idx, { value: e.target.value })}
                placeholder="value"
                disabled={disabled}
                className="bg-surface text-white flex-1"
              />
              {!disabled && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeRow(idx)}
                  className="h-8 w-8 p-0 text-muted hover:text-error hover:bg-surface-hover shrink-0"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
